import React, { useEffect, useMemo, useState } from 'react';
import {
  ASSET_TYPE,
  AssetItem,
  AssetReport,
  LIABILITY_TYPE,
  LiabilityItem,
  REPORT_ITEM_TYPE,
  REPORT_OWNER_TYPE,
  ReportItem,
  ReportOwnerType,
} from '../../type/AssetReport.type';
import AddAssetReportModalInput from './AddAssetReportModalInput';
import AssetReportModalEditTable from './AssetReportModalEditTable';
import { useAssetReportAPI } from '../../api/AssetReport.api';

export interface AddAssetReportModalData {
  isOpen: boolean;
  onClose: () => void;
}

const AddAssetReportModal = ({ isOpen, onClose }: AddAssetReportModalData) => {
  const { insertAssetReport } = useAssetReportAPI();
  const [newAssetReport, setNewAssetReport] = useState<AssetReport>({
    id: '',
    yearMonth: new Date().toISOString().slice(0, 7),
    comment: '',
    timestamp: Date.now(),
    reportItemList: [],
  });
  const [activeDataTab, setActiveDataTab] = useState<ReportOwnerType>(REPORT_OWNER_TYPE.HUSBAND);

  useEffect(() => {
    setNewAssetReport({
      id: '',
      yearMonth: new Date().toISOString().slice(0, 7),
      comment: '',
      timestamp: Date.now(),
      reportItemList: [],
    });
  }, [isOpen]);

  const duplicateNameList = useMemo(
    () =>
      Array.from(
        new Set(
          newAssetReport.reportItemList
            .map((reportItem) => reportItem.name + '!!!!' + reportItem.type + '!!!!' + reportItem.owner)
            .filter((item, idx, self) => self.indexOf(item) !== idx),
        ),
      ),
    [newAssetReport.reportItemList],
  );

  const handleNewAssetReportChange = (target: string, value: any) => {
    setNewAssetReport({
      ...newAssetReport,
      [target]: value,
    });
  };

  const handleCloseClick = () => {
    onClose();
  };

  const handleReportItemChange = (index: number, field: any, value: string | number) => {
    newAssetReport.reportItemList[index] = {
      ...newAssetReport.reportItemList[index],
      [field]: value,
    };
    handleNewAssetReportChange('reportItemList', [...newAssetReport.reportItemList]);
  };

  const handleDeleteReportItem = (index: number) => {
    handleNewAssetReportChange(
      'reportItemList',
      newAssetReport.reportItemList.filter((reportItem, itemIndex) => itemIndex !== index),
    );
  };

  const handleAddClick = async () => {
    const additionalExpectedToConsumeLiabilityItemList = newAssetReport.reportItemList
      .filter(
        (reportItem) =>
          reportItem.type === REPORT_ITEM_TYPE.ASSET &&
          (reportItem as AssetItem).assetType === ASSET_TYPE.EXPECT_TO_CONSUME,
      )
      .map(
        (reportItem) =>
          ({
            owner: reportItem.owner,
            name: reportItem.name,
            comment: '',
            type: REPORT_ITEM_TYPE.LIABILITY,
            amount: reportItem.amount,
            excludeYn: reportItem.excludeYn,
            liabilityType: LIABILITY_TYPE.EXPECT_TO_CONSUME,
          }) as LiabilityItem,
      );
    const { data: assetReportId } = await insertAssetReport({
      ...newAssetReport,
      reportItemList: [
        ...newAssetReport.reportItemList.map((reportItem) => ({ ...reportItem, prevExistYn: false })),
        ...additionalExpectedToConsumeLiabilityItemList,
      ],
    });

    if (assetReportId) {
      onClose();
    }
  };

  return isOpen ? (
    <div className="fixed min-h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">자산 추가</h2>
            <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <AddAssetReportModalInput
          newAssetReport={newAssetReport}
          handleNewAssetReportChange={handleNewAssetReportChange}
        />
        <div className="border-t border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">업로드된 데이터</h3>
            <div className="mb-4 border-b border-gray-200">
              <nav className="grid grid-cols-2 w-full" aria-label="Tabs">
                <button
                  onClick={() => setActiveDataTab(REPORT_OWNER_TYPE.HUSBAND)}
                  className={`py-3 text-center text-sm font-medium ${
                    activeDataTab === REPORT_OWNER_TYPE.HUSBAND
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  남편
                </button>
                <button
                  onClick={() => setActiveDataTab(REPORT_OWNER_TYPE.WIFE)}
                  className={`py-3 text-center text-sm font-medium ${
                    activeDataTab === REPORT_OWNER_TYPE.WIFE
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  아내
                </button>
              </nav>
            </div>
            <div className="overflow-x-auto">
              <AssetReportModalEditTable
                owner={activeDataTab}
                duplicateNameList={duplicateNameList}
                reportItemList={newAssetReport.reportItemList}
                handleDeleteReportItem={handleDeleteReportItem}
                handleReportItemChange={handleReportItemChange}
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center text-gray-600 mb-3">
            <i className="fas fa-exclamation-circle mr-2"></i>
            <span className="text-sm font-medium">지출예정 자산은 자동으로 지출예정 부채가 추가됩니다.</span>
          </div>
          {duplicateNameList.length > 0 && (
            <div className="flex items-center text-red-600 mb-3">
              <i className="fas fa-exclamation-circle mr-2"></i>
              <span className="text-sm font-medium">
                동일 이름의 자산/부채는 존재할 수 없습니다! <br />
                {duplicateNameList.map((duplicateName) => (
                  <>
                    - {duplicateName.split('!!!!')[0]}
                    <br />
                  </>
                ))}
              </span>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCloseClick}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 !rounded-button"
            >
              취소
            </button>
            <button
              onClick={handleAddClick}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 !rounded-button"
              disabled={duplicateNameList.length > 0}
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default AddAssetReportModal;
